import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Cpu, Gauge, Weight, Zap, Eye, Brain, Hand, Activity, Users } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import RoboticsConsultationForm from "@/components/RoboticsConsultationForm";
import puduD9Img from "@/assets/robots/pudu-d9.png";
import puduD7Img from "@/assets/robots/pudu-d7.png";
import { ComparisonSection, LeadCaptureCTA } from "./PuduD7";

const PuduD9 = () => {
  const [formOpen, setFormOpen] = useState(false);

  const specs = [
    { icon: <Weight className="w-5 h-5" />, label: "Height", value: "170 cm / 5'6\"" },
    { icon: <Weight className="w-5 h-5" />, label: "Weight", value: "65 kg / 143 lbs" },
    { icon: <Cpu className="w-5 h-5" />, label: "Degrees of Freedom", value: "42" },
    { icon: <Zap className="w-5 h-5" />, label: "Maximum Payload", value: "20 kg / 44 lbs" },
    { icon: <Gauge className="w-5 h-5" />, label: "Max Walking Speed", value: "2 m/s" },
    { icon: <Brain className="w-5 h-5" />, label: "Computing Power", value: "275 TOPS" },
    { icon: <Eye className="w-5 h-5" />, label: "Sensors", value: "RGB, RGBD, Tactile, IMU" },
    { icon: <Hand className="w-5 h-5" />, label: "Dexterous Hand", value: "PUDU DH11" },
  ];

  const valuePoints = [
    { icon: <Users className="w-6 h-6" />, title: "Full-Scale Humanoid Design", desc: "Adult human proportions engineered for natural integration into spaces built for people." },
    { icon: <Activity className="w-6 h-6" />, title: "Human-Like Bipedal Walking", desc: "Dynamic balance and stable bipedal locomotion for real-world environments and uneven terrain." },
    { icon: <Hand className="w-6 h-6" />, title: "Advanced Object Manipulation", desc: "PUDU DH11 dexterous hands deliver precision handling, fine motor control, and adaptive grip." },
    { icon: <Eye className="w-6 h-6" />, title: "Real-Time Perception & Navigation", desc: "Multi-sensor fusion across RGB, RGBD, tactile, and IMU for continuous spatial awareness." },
    { icon: <Brain className="w-6 h-6" />, title: "Built for Future Commercial Use", desc: "Designed for commercial pilots, research, hospitality, healthcare, logistics, and service applications." },
    { icon: <Users className="w-6 h-6" />, title: "Designed to Support Human Teams", desc: "Built to augment workforces — reducing burnout and repetitive load, not replacing people." },
  ];

  return (
    <>
      <SEO
        title="PUDU D9 Full-Sized Bipedal Humanoid Robot | Neil AI Global Solutions"
        description="PUDU D9 commercially viable bipedal humanoid robot: 170cm, 42 DOF, 20kg payload, 275 TOPS, PUDU DH11 dexterous hands. Deployed by Neil AI — PUDU Robotics authorized dealer."
        keywords="PUDU D9 humanoid robot, humanoid robot for business, PUDU Robotics authorized dealer, commercial robotics North Carolina, AI robotics company North Carolina, bipedal humanoid robot, Neil AI Global Solutions robotics"
        canonical="https://www.neilai.ai/robots/pudu-d9"
      />
      <Navigation />

      <main className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 pt-32 md:pt-40 pb-12">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/robots"><ArrowLeft className="mr-2 w-4 h-4" />Back to Robots</Link>
          </Button>

          {/* Hero */}
          <section className="grid lg:grid-cols-2 gap-10 mb-20 items-center">
            <div className="relative bg-gradient-to-br from-white/5 to-primary/10 rounded-2xl p-8 flex items-center justify-center border border-[hsl(var(--line))] min-h-[420px]">
              <img
                src={puduD9Img}
                alt="PUDU D9 full-sized bipedal humanoid robot — Neil AI Global Solutions"
                className="w-full h-auto max-h-[600px] object-contain drop-shadow-[0_10px_40px_rgba(0,163,255,0.25)]"
                loading="eager"
              />
              <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground font-bold px-3 py-1">
                PUDU Robotics Authorized Dealer
              </Badge>
            </div>

            <div className="space-y-6">
              <p className="text-primary font-semibold tracking-wider uppercase text-sm">Full-Sized Bipedal Humanoid Robot</p>
              <h1 className="font-orbitron font-bold text-3xl md:text-5xl lg:text-6xl leading-tight">PUDU D9</h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                The PUDU D9 is Pudu Robotics' full-sized bipedal humanoid robot designed for commercially viable embodied intelligence, advanced mobility, object manipulation, customer interaction, research, hospitality, healthcare, logistics, and future service applications.
              </p>
              <div className="rounded-xl border border-[hsl(var(--line))] bg-white/5 p-5">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <span className="text-foreground font-semibold">Powered by Neil AI Global Solutions.</span> We help organizations scope, pilot, and deploy commercially viable humanoid robotics — supporting human teams across service, logistics, and operations.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="glow-gold flex-1" onClick={() => setFormOpen(true)}>Request D9 Information</Button>
                <Button asChild size="lg" variant="outline" className="flex-1">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">Book a Humanoid Robotics Demo</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="flex-1">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">Talk to Neil AI</a>
                </Button>
              </div>
            </div>
          </section>

          {/* Specs */}
          <section className="mb-20">
            <div className="mb-10 text-center max-w-3xl mx-auto">
              <h2 className="font-orbitron text-2xl md:text-4xl font-bold mb-3">Technical Specifications</h2>
              <p className="text-muted-foreground">Engineered for commercially viable embodied intelligence.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {specs.map((s, i) => (
                <Card key={i} className="glass-effect border-[hsl(var(--line))]">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 text-primary mb-2">{s.icon}<span className="text-xs uppercase tracking-wider font-semibold">{s.label}</span></div>
                    <p className="text-sm md:text-base font-bold">{s.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Value Points */}
          <section className="mb-20">
            <div className="mb-10 text-center max-w-3xl mx-auto">
              <h2 className="font-orbitron text-2xl md:text-4xl font-bold mb-3">Commercially Viable Humanoid Intelligence</h2>
              <p className="text-muted-foreground">A platform built for service, research, and the next generation of human-robot collaboration.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {valuePoints.map((v, i) => (
                <Card key={i} className="glass-effect border-[hsl(var(--line))] hover:border-primary/40 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="text-primary mb-3">{v.icon}</div>
                    <h3 className="font-bold text-lg mb-2">{v.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Comparison */}
          <ComparisonSection puduD7Img={puduD7Img} puduD9Img={puduD9Img} />

          {/* Lead capture */}
          <LeadCaptureCTA onOpen={() => setFormOpen(true)} />
        </div>
      </main>

      <Footer />
      <RoboticsConsultationForm open={formOpen} onOpenChange={setFormOpen} defaultRobot="PUDU D9" />
    </>
  );
};

export default PuduD9;
